import React from 'react';
import { notificationApi } from '../services/api';

export type AppNotificationAttachment = {
  id: string;
  name: string;
  type?: string;
  size?: number;
  downloadPath?: string;
};

export type AppNotification = {
  id: string;
  title: string;
  body: string;
  severity?: string | null;
  recipientType?: string | null;
  readAt?: string | null;
  dismissedAt?: string | null;
  createdAt?: string | null;
  publishedAt?: string | null;
  createdByName?: string | null;
  targetSummary?: string | null;
  attachments?: AppNotificationAttachment[];
};

type Props = {
  notifications: AppNotification[];
  formatDateTime?: (value?: string | Date | null) => string | null;
  formatAttachmentSize?: (size: number) => string;
  onDismiss: (notification: AppNotification) => void | Promise<void>;
  onOpenAttachment?: (notification: AppNotification, attachment: AppNotificationAttachment) => void | Promise<void>;
  onReview?: () => void;
  styles?: any;
};

type AttachmentPreview = {
  url: string;
  type: string;
  name: string;
  error?: string;
};

function mergeStyles(...entries: Array<React.CSSProperties | undefined | null | false>) {
  return Object.assign({}, ...entries.filter(Boolean));
}

function defaultFormatAttachmentSize(size?: number) {
  const value = Number(size || 0);
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
}

function isPreviewableAttachment(attachment: AppNotificationAttachment) {
  const type = String(attachment.type || '').toLowerCase();
  const name = String(attachment.name || '').toLowerCase();
  return type.startsWith('image/') || type === 'application/pdf' || name.endsWith('.pdf');
}

export function AppNotificationPopups({
  notifications,
  formatDateTime,
  formatAttachmentSize,
  onDismiss,
  onOpenAttachment,
  onReview,
  styles
}: Props) {
  const activeNotification = notifications.find(notification => !notification.dismissedAt);
  const [attachmentPreviews, setAttachmentPreviews] = React.useState<Record<string, AttachmentPreview>>({});
  const [attachmentPreviewLoading, setAttachmentPreviewLoading] = React.useState<Record<string, boolean>>({});
  const previewUrlsRef = React.useRef<string[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setAttachmentPreviews({});
    setAttachmentPreviewLoading({});

    const activeAttachments = Array.isArray(activeNotification?.attachments) ? activeNotification?.attachments || [] : [];
    const previewableAttachments = activeAttachments.filter(
      (attachment): attachment is AppNotificationAttachment => !!attachment?.id && isPreviewableAttachment(attachment)
    );
    if (!activeNotification?.id || previewableAttachments.length === 0) {
      return () => {
        cancelled = true;
      };
    }

    setAttachmentPreviewLoading(
      previewableAttachments.reduce<Record<string, boolean>>((acc, attachment) => {
        acc[attachment.id] = true;
        return acc;
      }, {})
    );

    previewableAttachments.forEach(async attachment => {
      try {
        const file = await notificationApi.fetchAttachment(activeNotification.id, attachment.id);
        if (cancelled) {
          URL.revokeObjectURL(file.url);
          return;
        }
        previewUrlsRef.current.push(file.url);
        setAttachmentPreviews(prev => ({
          ...prev,
          [attachment.id]: {
            url: file.url,
            type: file.contentType || attachment.type || 'application/octet-stream',
            name: attachment.name
          }
        }));
      } catch (err: any) {
        if (!cancelled) {
          setAttachmentPreviews(prev => ({
            ...prev,
            [attachment.id]: {
              url: '',
              type: attachment.type || 'application/octet-stream',
              name: attachment.name,
              error: err?.message || 'Attachment preview could not be loaded.'
            }
          }));
        }
      } finally {
        if (!cancelled) {
          setAttachmentPreviewLoading(prev => ({ ...prev, [attachment.id]: false }));
        }
      }
    });

    return () => {
      cancelled = true;
      previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
    };
  }, [activeNotification?.id]);

  if (!activeNotification) return null;

  const count = notifications.filter(notification => !notification.dismissedAt).length;
  const timestamp =
    formatDateTime?.(activeNotification.publishedAt || activeNotification.createdAt || null) ||
    activeNotification.publishedAt ||
    activeNotification.createdAt ||
    '';
  const severity = String(activeNotification.severity || 'info').toLowerCase();
  const attachments = Array.isArray(activeNotification.attachments) ? activeNotification.attachments : [];
  const formatSize = formatAttachmentSize || defaultFormatAttachmentSize;

  const overlayStyle = mergeStyles(
    {
      position: 'fixed',
      inset: 0,
      zIndex: 12000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem',
      backgroundColor: 'rgba(15, 23, 42, 0.55)'
    },
    styles?.historyOverlay,
    styles?.modal,
    { zIndex: 12000 }
  );
  const panelStyle = mergeStyles(
    {
      backgroundColor: '#ffffff',
      borderRadius: '0.75rem',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.25)',
      width: 'min(620px, 100%)',
      maxHeight: 'calc(100vh - 3rem)',
      overflow: 'auto'
    },
    styles?.modalContent,
    styles?.messagesModalCard,
    styles?.historyPanel,
    {
      maxWidth: attachments.length > 0 ? '760px' : '620px',
      width: attachments.length > 0 ? 'min(760px, 100%)' : 'min(620px, 100%)'
    }
  );
  const headerStyle = mergeStyles(
    {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '1rem',
      paddingBottom: '1rem',
      marginBottom: '1rem',
      borderBottom: '1px solid #e5e7eb'
    },
    styles?.modalHeader,
    styles?.messagesModalHeader,
    styles?.historyHeader,
    { marginBottom: 0 }
  );
  const contentStyle = mergeStyles(
    {
      display: 'grid',
      gap: '1rem',
      padding: '0 0 0'
    },
    styles?.historyBody
  );
  const titleStyle = mergeStyles(
    {
      margin: 0
    },
    styles?.modalTitle,
    styles?.historyModalTitle,
    styles?.messagesComposeTitle
  );
  const subtitleStyle = mergeStyles(
    {
      color: '#6b7280',
      fontSize: '0.95rem',
      marginTop: '0.35rem'
    },
    styles?.historySubtitle,
    styles?.messagesComposeSubtitle,
    styles?.messagesItemMeta
  );
  const severityStyle = mergeStyles(
    {
      flex: '0 0 auto',
      borderRadius: '999px',
      border: '1px solid #d8d0c1',
      backgroundColor: '#fffdf8',
      color: '#0f3f46',
      padding: '0.25rem 0.65rem',
      fontSize: '0.78rem',
      fontWeight: 700,
      textTransform: 'capitalize'
    },
    styles?.pill,
    styles?.statusBadge
  );
  const bodyStyle = mergeStyles(
    {
      color: '#17252a',
      whiteSpace: 'pre-wrap',
      lineHeight: 1.55
    },
    styles?.messagesDetailBody
  );
  const metaStyle = mergeStyles(
    {
      color: '#6b7280',
      fontSize: '0.88rem'
    },
    styles?.historySubtitle,
    styles?.messagesItemMeta
  );
  const actionsStyle = mergeStyles(
    {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      flexWrap: 'wrap',
      marginTop: '0.25rem'
    },
    styles?.modalActions,
    styles?.messagesReplyActions
  );
  const secondaryButtonStyle = mergeStyles(
    styles?.button,
    styles?.secondaryButton,
    styles?.messagesReplyButtonSecondary,
    styles?.messagesReplySecondaryButton,
    styles?.portalSecondaryButton
  );
  const primaryButtonStyle = mergeStyles(
    styles?.button,
    styles?.primaryButton,
    styles?.messagesReplyButton,
    styles?.portalPrimaryButton
  );
  const attachmentListStyle = mergeStyles(
    {
      display: 'grid',
      gap: '0.85rem'
    },
    styles?.messagesAttachmentList
  );
  const attachmentItemStyle = mergeStyles(
    {
      display: 'grid',
      gap: '0.65rem',
      border: '1px solid #d8cfc1',
      borderRadius: '0.85rem',
      background: '#fffdf8',
      padding: '0.75rem'
    },
    styles?.messagesAttachmentItem,
    {
      display: 'grid',
      alignItems: 'stretch',
      justifyContent: 'stretch'
    }
  );
  const attachmentHeaderStyle = mergeStyles(
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '0.75rem',
      flexWrap: 'wrap'
    },
    styles?.messagesItemHeader
  );
  const attachmentNameStyle = mergeStyles(
    {
      color: '#0f3f46',
      fontWeight: 700
    },
    styles?.messagesItemTitle
  );
  const attachmentPreviewShellStyle = mergeStyles({
    overflow: 'hidden',
    border: '1px solid #e0d6c8',
    borderRadius: '0.75rem',
    background: '#ffffff'
  });
  const attachmentButtonStyle = mergeStyles(
    styles?.messagesAttachmentRemove,
    styles?.inlineLinkButton,
    {
      textAlign: 'left',
      cursor: onOpenAttachment ? 'pointer' : 'default'
    }
  );

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="app-notification-title">
      <div style={panelStyle}>
        <div style={headerStyle}>
          <div>
            <h2 id="app-notification-title" style={titleStyle}>{activeNotification.title}</h2>
            <div style={subtitleStyle}>Notification</div>
          </div>
          <span style={severityStyle}>{severity}</span>
        </div>
        <div style={contentStyle}>
          <div style={bodyStyle}>{activeNotification.body}</div>
          <div style={metaStyle}>
            {timestamp ? `Published ${timestamp}` : 'Published recently'}
            {activeNotification.createdByName ? ` by ${activeNotification.createdByName}` : ''}
            {count > 1 ? ` - ${count - 1} more pending` : ''}
          </div>
          {attachments.length > 0 && (
            <div style={attachmentListStyle}>
              {attachments.map(attachment => (
                <div key={attachment.id} style={attachmentItemStyle}>
                  <div style={attachmentHeaderStyle}>
                    <div>
                      <div style={attachmentNameStyle}>{attachment.name}</div>
                      <div style={metaStyle}>
                        {attachment.type || 'file'} | {formatSize(Number(attachment.size || 0))}
                      </div>
                    </div>
                    <button
                      type="button"
                      style={attachmentButtonStyle}
                      onClick={() => onOpenAttachment?.(activeNotification, attachment)}
                      disabled={!onOpenAttachment}
                    >
                      Open / download
                    </button>
                  </div>
                  {isPreviewableAttachment(attachment) && (
                    <div style={attachmentPreviewShellStyle}>
                      {attachmentPreviewLoading[attachment.id] ? (
                        <div style={{ ...metaStyle, padding: '1rem' }}>Loading attachment preview...</div>
                      ) : attachmentPreviews[attachment.id]?.error ? (
                        <div style={{ ...metaStyle, padding: '1rem', color: '#a33b2f' }}>{attachmentPreviews[attachment.id].error}</div>
                      ) : attachmentPreviews[attachment.id]?.url ? (
                        String(attachmentPreviews[attachment.id].type || '').toLowerCase().startsWith('image/') ? (
                          <img
                            src={attachmentPreviews[attachment.id].url}
                            alt={attachment.name}
                            style={{ display: 'block', width: '100%', maxHeight: '520px', objectFit: 'contain' }}
                          />
                        ) : (
                          <iframe
                            title={attachment.name}
                            src={`${attachmentPreviews[attachment.id].url}#toolbar=0&navpanes=0&page=1`}
                            style={{ display: 'block', width: '100%', minHeight: '460px', border: 0 }}
                          />
                        )
                      ) : (
                        <div style={{ ...metaStyle, padding: '1rem' }}>Attachment preview unavailable.</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={actionsStyle}>
            {onReview && (
              <button type="button" style={secondaryButtonStyle} onClick={onReview}>
                Review later
              </button>
            )}
            <button type="button" style={primaryButtonStyle} onClick={() => onDismiss(activeNotification)}>
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
